import React from 'react';
import HybridSeoPage from '../components/HybridSeoPage';
import { Link } from 'react-router-dom';

const HybridJavaPlaygroundPage = () => {
  const seoContent = (
    <div>
      <h2>Welcome to Your Java Coding Space!</h2>
      <p>
        This is your personal Java playground where learning and fun come together! Think of it as a 
        friendly sandbox where you can experiment with Java code without any pressure. Whether you're 
        just starting your programming journey or want to practice new concepts, this playground provides 
        a perfect environment to write, test, and run Java programs directly in your browser.
      </p>
      <p>
        The best part about our playground? No setup required! You don't need to install anything or 
        configure complicated settings. Just open your browser, type your code, and click run. Our system 
        handles all the technical details behind the scenes, so you can focus on what really matters - 
        learning and having fun with Java programming!
      </p>

      <h2>Practice with These Fun Examples</h2>
      <p>
        Let's explore some simple and fun examples that are perfect for learning Java programming. 
        These programs are designed to be beginner-friendly and will help you understand basic concepts 
        while having fun with code!
      </p>

      <h3>Example 1: Hello, Friends!</h3>
      <pre><code>{`public class HelloFriends {
    public static void main(String[] args) {
        System.out.println("Hello, Java World!");
        System.out.println("I'm learning to code!");
        System.out.println("This is so much fun! 🎉");
        
        // Let's count to 5
        for (int i = 1; i <= 5; i++) {
            System.out.println("Count: " + i);
        }
    }
}`}</code></pre>
      <p>
        <strong>What this does:</strong><br/>
        This friendly program says hello to the world and counts to 5! It's a great way to start 
        learning Java. Try changing the messages or counting to a different number. You can even 
        add your own fun messages to make it your own!
      </p>
      <p>
        <strong>Fun challenge:</strong> Can you make it count down from 5 to 1 instead?
      </p>

      <h3>Example 2: Number Magic</h3>
      <pre><code>{`public class NumberMagic {
    public static void main(String[] args) {
        int favoriteNumber = 7;
        
        System.out.println("My favorite number is: " + favoriteNumber);
        
        // Let's do some magic math!
        int doubled = favoriteNumber * 2;
        System.out.println("Double the magic: " + doubled);
        
        int squared = favoriteNumber * favoriteNumber;
        System.out.println("Square the magic: " + squared);
        
        // Is it even or odd?
        if (favoriteNumber % 2 == 0) {
            System.out.println("It's an even number! ✨");
        } else {
            System.out.println("It's an odd number! 🌟");
        }
    }
}`}</code></pre>
      <p>
        <strong>What this does:</strong><br/>
        This program performs some fun math tricks with your favorite number! It doubles the number, 
        squares it, and even tells you if it's even or odd. Try changing the favoriteNumber to see 
        what happens with different numbers.
      </p>
      <p>
        <strong>Fun challenge:</strong> Can you add a check to see if the number is positive, negative, 
        or zero?
      </p>

      <h3>Example 3: Story Generator</h3>
      <pre><code>{`public class StoryGenerator {
    public static void main(String[] args) {
        String hero = "Alex";
        String place = "magical forest";
        String treasure = "golden key";
        
        System.out.println("📖 Once upon a time...");
        System.out.println("There was a brave hero named " + hero);
        System.out.println(hero + " explored a " + place);
        System.out.println("And found a mysterious " + treasure + "!");
        
        System.out.println("\\n🎭 The End");
        System.out.println("What happens next is up to you!");
        
        // Bonus: Count the characters in the story
        int storyLength = hero.length() + place.length() + treasure.length();
        System.out.println("\\n📏 Story has " + storyLength + " characters in names!");
    }
}`}</code></pre>
      <p>
        <strong>What this does:</strong><br/>
        Create your own mini-story! This program generates a simple story with a hero, place, 
        and treasure. You can change any of these to create your own unique story. It even counts 
        how many characters are in your story elements!
      </p>
      <p>
        <strong>Fun challenge:</strong> Can you add more story elements like a villain, a sidekick, or 
        a magical power?
      </p>

      <h3>Example 4: Fun Calculator Game</h3>
      <pre><code>{`public class FunCalculator {
    public static void main(String[] args) {
        int score = 0;
        
        System.out.println("🎮 Welcome to the Math Game!");
        
        // Round 1: Addition
        int a = 5, b = 3;
        int answer = a + b;
        System.out.println("Round 1: " + a + " + " + b + " = ?");
        System.out.println("Answer: " + answer + " ✓");
        score += 10;
        
        // Round 2: Multiplication
        int c = 4, d = 6;
        int answer2 = c * d;
        System.out.println("\\nRound 2: " + c + " × " + d + " = ?");
        System.out.println("Answer: " + answer2 + " ✓");
        score += 10;
        
        // Round 3: Bonus round!
        int e = 20, f = 4;
        int answer3 = e / f;
        System.out.println("\\nBonus Round: " + e + " ÷ " + f + " = ?");
        System.out.println("Answer: " + answer3 + " ✓");
        score += 15;
        
        System.out.println("\\n🏆 Final Score: " + score + " points!");
        System.out.println("You're a math champion! 🌟");
    }
}`}</code></pre>
      <p>
        <strong>What this does:</strong><br/>
        It's a math game that tests your skills! The program asks questions and gives you answers, 
        while keeping score. You can modify the numbers to create your own math challenges. Try 
        adding more rounds or different types of math problems!
      </p>
      <p>
        <strong>Fun challenge:</strong> Can you add subtraction and remainder (%) problems to the game?
      </p>

      <h2>Tips for Learning in the Playground</h2>
      <p>
        Learning Java is like learning to speak a new language - it takes practice and patience! Here 
        are some friendly tips to make your learning journey amazing:
      </p>
      <p>
        <strong>Start Small:</strong> Begin with simple programs like the examples above. Once you're 
        comfortable, try making small changes. Change a message, modify a number, or add a new line 
        of code. Small victories build confidence!
      </p>
      <p>
        <strong>Experiment Freely:</strong> This is your playground, so feel free to try anything! 
        Break things, fix them, and learn from your mistakes. Every error message is just a hint 
        pointing you toward the solution. Don't worry about making mistakes - they're part of learning!
      </p>
      <p>
        <strong>Play with Variables:</strong> Try changing variable values and see what happens. 
        What if you change a number from 5 to 100? What happens if you change text from "Hello" to 
        "Hi there"? Experimenting helps you understand how code works.
      </p>
      <p>
        <strong>Add Comments:</strong> Use // to add notes to your code. Comments help you remember 
        what different parts of your program do. They're like leaving breadcrumbs for your future 
        self to follow!
      </p>
      <p>
        <strong>Share Your Creations:</strong> Show your friends what you've made! Teaching someone 
        else is one of the best ways to learn. Plus, it's fun to see what cool programs your friends 
        create too!
      </p>

      <h2>Ready for More Adventures?</h2>
      <p>
        You've learned the basics, and now you're ready for even more Java adventures! Our playground 
        is just the beginning of your coding journey. There are so many amazing things you can create 
        with Java - games, calculators, stories, and so much more!
      </p>
      <p>
        When you're ready to learn more advanced topics, <Link to="/java-online-compiler">explore our 
        online Java compiler</Link> to understand how compilation works, or <Link to="/run-java-code-online">discover 
        how to run Java code online</Link> with more complex examples. The coding world is full of 
        exciting possibilities!
      </p>
      <p>
        Remember: every expert programmer started exactly where you are now - curious, excited, and 
        ready to learn. Keep practicing, stay curious, and most importantly, have fun with your code! 
        Happy coding! 🚀
      </p>
    </div>
  );

  return (
    <HybridSeoPage
      title="Code by Skillora - Java Playground | Quantum Stack"
      metaDescription="Code by Skillora - Best Java playground by Quantum Stack. Practice Java programming online in a fun, interactive environment. Write, run, and test Java code instantly. Perfect for beginners and developers learning Java."
      heroTitle="Java Playground"
      heroDescription="Your friendly space to practice Java programming! Write, run, and experiment with code in a fun, no-pressure environment perfect for learning."
      quickGuideTitle="How to Play in the Java Playground"
      seoContent={seoContent}
      defaultCode={`public class MyFirstProgram {
    public static void main(String[] args) {
        System.out.println("🎉 Welcome to Java Playground!");
        System.out.println("I'm ready to learn and have fun!");
        
        // Let's try something fun!
        int myNumber = 42;
        System.out.println("My lucky number is: " + myNumber);
        
        System.out.println("Let's start coding together! 🚀");
    }
}`}
    />
  );
};

export default HybridJavaPlaygroundPage;
